import TextField from "@mui/material/TextField";
import { useRef, useState, useMemo, useCallback } from "react";
import "./index.css";
import { useTranslation } from "react-i18next";
import Menu from "@mui/material/Menu";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandMore from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";

const EditableResultDropdownSubgroups = ({
  chosen,
  classStyle = "newreact__select__azilis",
  setValue,
  disabled = false,
  propertyName = "value",
  styleNo = 1,
  scrollLeft = 0,
  options,
  check,
  edit,
}) => {
  const divRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSubgroup, setSelectedSubgroup] = useState(null);
  const { i18n } = useTranslation();

  const getSubgroupKey = useCallback(
    () => (i18n.language === "en" ? "subgroup_en" : "subgroup"),
    [i18n.language]
  );

  const groupedOptions = useMemo(() => {
    const subgroupKey = getSubgroupKey();
    return options.reduce((acc, option) => {
      const category = option[subgroupKey] || option.category || "";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(option);
      return acc;
    }, {});
  }, [options, getSubgroupKey]);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedSubgroup(null);
  }, []);

  const handleSubgroupClick = useCallback(
    (subgroup) => {
      setSelectedSubgroup(subgroup === selectedSubgroup ? null : subgroup);
    },
    [selectedSubgroup]
  );

  const handleOptionSelect = useCallback(
    (option) => {
      setValue(option);
      handleClose();
    },
    [setValue, handleClose]
  );

  const displayValue = useMemo(
    () =>
      check === "noProperty"
        ? chosen?.title || chosen || ""
        : chosen?.[propertyName] || "",
    [check, chosen, propertyName]
  );

  return (
    <>
      {!edit ? (
        <h4>{displayValue}</h4>
      ) : (
        <div className="w-100" ref={divRef}>
          <TextField
            value={displayValue}
            onClick={handleClick}
            disabled={disabled}
            className={classStyle + " azilis__dropdown__style" + styleNo}
            InputProps={{
              readOnly: true,
              sx: {
                fontSize: "12px",
                cursor: "pointer",
                padding: "5px",
              },
            }}
            sx={{ width: "100%", cursor: "pointer" }}
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                width: 300,
                maxHeight: "70vh",
                bgcolor: "white",
                boxShadow:
                  "0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)",
                "& .MuiList-root": {
                  padding: 0,
                },
                "& .MuiListItem-root": {
                  padding: "8px 16px",
                },
              },
              className: "dropdown-bg-custom",
            }}
            transitionDuration={0}
            MenuListProps={{
              sx: {
                padding: 0,
              },
            }}
          >
            <List component="nav" dense>
              {Object.entries(groupedOptions).map(
                ([subgroup, subgroupOptions]) => (
                  <div key={subgroup}>
                    <ListItem
                      button
                      onClick={() => handleSubgroupClick(subgroup)}
                      sx={{
                        bgcolor:
                          selectedSubgroup === subgroup
                            ? "rgba(0, 0, 0, 0.04)"
                            : "transparent",
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.04)",
                        },
                        borderBottom: "none",
                      }}
                    >
                      <ListItemText
                        primary={subgroup}
                        primaryTypographyProps={{
                          style: { fontWeight: "600", fontSize: "12px" },
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          padding: 0,
                          transform:
                            selectedSubgroup === subgroup
                              ? "rotate(180deg)"
                              : "rotate(0)",
                          transition: "none",
                        }}
                      >
                        <ExpandMore />
                      </IconButton>
                    </ListItem>
                    <Collapse in={selectedSubgroup === subgroup} timeout={0}>
                      <List component="div" disablePadding>
                        {subgroupOptions.map((option) => (
                          <ListItem
                            button
                            key={option.title}
                            onClick={() => handleOptionSelect(option)}
                            sx={{
                              pl: 4,
                              fontSize: "12px",
                              "&:hover": {
                                bgcolor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                          >
                            <ListItemText
                              primary={option.title}
                              primaryTypographyProps={{
                                style: { fontSize: "12px" },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </div>
                )
              )}
            </List>
          </Menu>
        </div>
      )}
    </>
  );
};

export default EditableResultDropdownSubgroups;
